import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '../../utils/cn';
import { Spinner } from '../Spinner/Spinner';

/**
 * Brief §5.1 variants:
 * - `primary`   the one main action of a view region — primary-700 fill, white text
 * - `secondary` white surface, 1px strong hairline, primary text
 * - `tertiary`  no border, primary-600 text (alias: `ghost`, kept for existing call sites)
 * - `danger`    destructive confirmation — danger-600 fill
 * - `warning`   caution action — warning-500 fill, dark text
 * - `soft`      primary-50 fill with primary-700 text (alias: `accent`, kept for existing call sites)
 * - `link`      inline text action in the link colour
 */
export type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'ghost' | 'danger' | 'warning' | 'soft' | 'accent' | 'link';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  /** Icon rendered before the label. Replaced by the spinner while loading, without a width change. */
  iconStart?: ReactNode;
  /** Icon rendered after the label. */
  iconEnd?: ReactNode;
  fullWidth?: boolean;
  children?: ReactNode;
}

/*
 * Tokens v3.
 *
 * Buttons are flat. Only `secondary` carries a hairline — `border-border-input`, the same edge as the field
 * chrome in `shared/fieldStyles`, so a button and a Select trigger line up in a toolbar. Every other variant is
 * fill alone; hover moves the background, never the geometry. Radius is the 6px control radius. Sizes come
 * from the density tokens (`h-control-*`), so `touch` density lifts every button past a 52px hit target.
 */
export const buttonBase =
  'zo-button relative inline-flex select-none items-center justify-center whitespace-nowrap rounded-md font-strong transition-[background-color,box-shadow,color,opacity] motion-fast focus-ring disabled:cursor-not-allowed aria-busy:cursor-progress';

/** Dimming is applied by the components, which keep a *loading* button at full strength. */
export const buttonDisabled = 'opacity-50';

/** One perceptual step of a surface toward the foreground — darkens in light mode, lightens in dark. */
const liftSurface2 = 'hover:bg-hover active:bg-active';
const liftDanger =
  'hover:bg-[color:color-mix(in_oklab,var(--zo-danger)_88%,var(--zo-fg))] active:bg-[color:color-mix(in_oklab,var(--zo-danger)_78%,var(--zo-fg))]';

const tertiary = 'bg-transparent text-accent-fg hover:bg-primary-subtle active:bg-selected-hover';
const soft = 'bg-primary-subtle text-primary-subtle-fg hover:bg-selected-hover active:bg-accent-border';

export const buttonVariants: Record<ButtonVariant, string> = {
  primary: 'bg-primary text-primary-fg hover:bg-primary-hover active:bg-primary-active',
  secondary: cn(
    'bg-surface-1 text-fg border border-border-strong hover:border-border-input-hover',
    liftSurface2,
  ),
  tertiary,
  ghost: tertiary,
  danger: cn('bg-danger text-fg-inverse', liftDanger),
  warning: 'bg-warning text-fg-inverse hover:bg-warning-fg active:bg-warning-fg',
  soft,
  accent: soft,
  link: 'h-auto bg-transparent px-0 text-accent-fg underline-offset-2 hover:underline',
};

/** Touch density must clear a 52px hit target even when the control tokens are overridden. */
const touchTarget = '[[data-density=touch]_&]:min-h-13';

export const buttonSizes: Record<ButtonSize, string> = {
  sm: cn('h-control-sm gap-1 px-2.5 text-sm', touchTarget),
  md: cn('h-control gap-1.5 px-control-x text-table', touchTarget),
  lg: cn('h-control-lg gap-2 px-4 text-base', touchTarget),
};

/** Square padding for a button that carries an icon and no label. */
const iconOnlySizes: Record<ButtonSize, string> = {
  sm: cn('h-control-sm w-control-sm gap-0 px-0', touchTarget, '[[data-density=touch]_&]:min-w-13'),
  md: cn('h-control w-control gap-0 px-0', touchTarget, '[[data-density=touch]_&]:min-w-13'),
  lg: cn('h-control-lg w-control-lg gap-0 px-0', touchTarget, '[[data-density=touch]_&]:min-w-13'),
};

const spinnerSize: Record<ButtonSize, number> = { sm: 14, md: 16, lg: 18 };

/** Icons never shrink and sit on the optical centre line of the label. */
const slotClass = 'zo-button-icon inline-flex shrink-0 items-center justify-center [&>svg]:shrink-0';

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', loading = false, iconStart, iconEnd, fullWidth, className, disabled, children, type = 'button', ...rest },
  ref,
) {
  const isDisabled = disabled || loading;
  const hasLabel = children != null && children !== false;
  const iconOnly = !hasLabel && (iconStart != null || iconEnd != null);
  const spinner = <Spinner aria-hidden size={spinnerSize[size]} className="zo-button-spinner" />;
  return (
    <button
      ref={ref}
      type={type}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      data-variant={variant}
      data-size={size}
      data-loading={loading || undefined}
      className={cn(
        buttonBase,
        buttonVariants[variant],
        variant !== 'link' && (iconOnly ? iconOnlySizes[size] : buttonSizes[size]),
        // A loading button is busy, not unavailable: it keeps its colour so the spinner reads as work in progress.
        isDisabled && !loading && buttonDisabled,
        fullWidth && 'w-full',
        className,
      )}
      {...rest}
    >
      {/*
       * Loading must not resize the button. With a leading icon the spinner is laid over that icon's slot;
       * without one it is centred over the whole button and the label — still rendered, still the accessible
       * name — simply fades out.
       */}
      {iconStart != null ? (
        <span className={cn(slotClass, 'relative')}>
          <span className={cn('inline-flex', loading && 'opacity-0')} aria-hidden={loading || undefined}>
            {iconStart}
          </span>
          {loading ? <span className="absolute inset-0 inline-flex items-center justify-center">{spinner}</span> : null}
        </span>
      ) : null}
      {hasLabel ? <span className={cn('zo-button-label truncate', loading && iconStart == null && 'opacity-0')}>{children}</span> : null}
      {iconEnd != null ? (
        <span className={cn(slotClass, loading && iconStart == null && !hasLabel && 'opacity-0')}>{iconEnd}</span>
      ) : null}
      {loading && iconStart == null ? (
        <span className="pointer-events-none absolute inset-0 inline-flex items-center justify-center">{spinner}</span>
      ) : null}
    </button>
  );
});
